import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PlayerProvider } from "@/context/PlayerContext";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/components/AppLayout";

import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Claim from "@/pages/Claim";
import Initialize from "@/pages/Initialize";
import PublicHelpDesk from "@/pages/PublicHelpDesk";
import ChangePassword from "@/pages/ChangePassword";
import Dashboard from "@/pages/Dashboard";
import Podcasts from "@/pages/Podcasts";
import NotesExport from "@/pages/NotesExport";
import Inbox from "@/pages/Inbox";
import Disclaimers from "@/pages/Disclaimers";
import Articles from "@/pages/Articles";
import Feedback from "@/pages/Feedback";
import HelpDesk from "@/pages/HelpDesk";
import Metrics from "@/pages/Metrics";
import Users from "@/pages/Users";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-page">
      <div className="flex items-end gap-1 h-8">
        {[0, 1, 2, 3, 4].map((i) => (
          <span key={i} className="eq-bar" style={{ animationDelay: `${i * 0.12}s` }} />
        ))}
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user, mustChange } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  if (mustChange) return <Navigate to="/change-password" replace />;
  return (
    <PlayerProvider>
      <AppLayout>{children}</AppLayout>
    </PlayerProvider>
  );
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Claim /></PublicOnly>} />
            <Route path="/setup" element={<Initialize />} />
            <Route path="/help" element={<PublicHelpDesk />} />
            <Route path="/disclaimers" element={<Disclaimers />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/app" element={<Protected><Dashboard /></Protected>} />
            <Route path="/app/podcasts" element={<Protected><Podcasts /></Protected>} />
            <Route path="/app/notes" element={<Protected><NotesExport /></Protected>} />
            <Route path="/app/inbox" element={<Protected><Inbox /></Protected>} />
            <Route path="/app/articles" element={<Protected><Articles /></Protected>} />
            <Route path="/app/feedback" element={<Protected><Feedback /></Protected>} />
            <Route path="/app/help-desk" element={<Protected><HelpDesk /></Protected>} />
            <Route path="/app/metrics" element={<Protected><Metrics /></Protected>} />
            <Route path="/app/users" element={<Protected><Users /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
